import { Outlet, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronUp } from 'lucide-react';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollManager } from '../../components/ScrollManager';
import { DailyInspirationStory } from '../../components/DailyInspirationStory';
import { MaintenancePage } from '../../components/MaintenancePage';
import { YimaChatbot } from '../../components/YimaChatbot';
import { usePublicSettings } from '@/hooks';
import { SiteBanner } from '../../components/SiteBanner';
import { PublicHeader } from './PublicHeader';
import { PublicFooter } from './PublicFooter';

export function PublicLayout() {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const location = useLocation();

  const { data: settings } = usePublicSettings();

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 10);
      setShowScrollTop(scrollY > 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const NAV_LINKS = [
    { href: '/', label: t('nav.home') },
    { href: '/about', label: t('nav.about') },
    { href: '/activities', label: t('nav.activities') },
    { href: '/events', label: t('nav.events') },
    { href: '/blog', label: t('nav.blog') },
    { href: '/contact', label: t('nav.contact') },
  ];

  const FOOTER_EXTRA_LINKS = [
    { href: '/history', label: t('nav.history') },
    { href: '/gallery', label: t('nav.gallery') },
    { href: '/my-status', label: t('nav.myStatus') },
    { href: '/yim-talk', label: t('nav.yimTalk') },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  if (settings?.KILLSWITCH_PUBLIC === 'true') {
    return <MaintenancePage type="maintenance" />;
  }

  const email = settings?.EMAIL || '';
  const phone = settings?.PHONE || '';
  const phone2 = settings?.PHONE_2 || '';
  const address = settings?.ADDRESS || '';
  const facebookUrl = settings?.FACEBOOK_URL || '';
  const instagramUrl = settings?.INSTAGRAM_URL || '';
  const xUrl = settings?.X_URL || '';
  const linkedinUrl = settings?.LINKEDIN_URL || '';

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader
        isScrolled={isScrolled}
        isMobileMenuOpen={isMobileMenuOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        navLinks={NAV_LINKS}
        isActive={isActive}
      />

      <main className="flex-1">
        <ScrollManager>
          <Outlet />
        </ScrollManager>
      </main>

      <div className="print:hidden">
        <DailyInspirationStory />
      </div>

      <PublicFooter
        navLinks={NAV_LINKS}
        footerExtraLinks={FOOTER_EXTRA_LINKS}
        email={email}
        phone={phone}
        phone2={phone2}
        address={address}
        facebookUrl={facebookUrl}
        instagramUrl={instagramUrl}
        xUrl={xUrl}
        linkedinUrl={linkedinUrl}
      />

      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-24 right-6 z-40 w-11 h-11 rounded-full bg-[#1E40AF]/70 text-white shadow-lg shadow-blue-500/30 flex items-center justify-center hover:bg-[#1E3A8A] hover:shadow-xl hover:scale-105 active:scale-95 transition-all duration-200 print:hidden"
            aria-label={t('common.scrollToTop')}
          >
            <ChevronUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>

      <SiteBanner />
      <YimaChatbot />
    </div>
  );
}

export default PublicLayout;
